import { Tooltip as AntdTooltip, Select, Tag } from "antd";
import { IS_AWS_ENV } from "constants/app.constants";
import { RIG_LAYER } from "constants/mapLayers.constants";
import { RIG_FRAC_FIELD } from "constants/rigFrac.constants";
import {
  selectLayerFilterSettings,
  selectRigFracFilterOptions,
  useMapStore
} from "stores/map";
import type { RigFracLayerFilter as RigFracLayerFilterSelections } from "stores/map";
import styled from "styled-components";
import {
  formatRigFracStateLabel,
  rigFracCountyFieldName,
  rigFracSubBasinFieldName
} from "utils/map/rigFracValueMapping";

import { useRigFracCountyLabel } from "hooks/rigfrac/useRigFracCountyLabel";

import { LayerSettingsRowItem } from "../nodes/StyledNodePartials";

export interface RigFracLayerFilterProps {
  layerId: string;
}

const FIELDS: ReadonlyArray<{
  key: keyof RigFracLayerFilterSelections;
  label: string;
  /**
   * Rendered on the Rigs layer only (EVA-107). A frac crew has no rig, so every
   * frac feature carries the substituted "n/a" — the dropdown would offer a single
   * option whose filter matches every frac crew, i.e. a control that can only do
   * nothing. Same reasoning as gating the chart group-by by metric.
   */
  rigOnly?: boolean;
  // EVA-338. Offered on AWS only; Azure keeps its pre-EVA-338 fields.
  awsOnly?: boolean;
}> = [
  { key: "operator", label: "Operator" },
  { key: "basin", label: "Basin" },
  {
    key: "subBasin",
    get label() {
      return rigFracSubBasinFieldName("Sub-Basin");
    }
  },
  { key: "country", label: "Country", awsOnly: true },
  { key: "stateProvince", label: "State/Province", awsOnly: true },
  {
    key: "county",
    get label() {
      return rigFracCountyFieldName("County");
    }
  },
  { key: "locationType", label: "Location Type" },
  { key: "rigTrajectory", label: "Rig Trajectory", rigOnly: true }
];

// Hoisted so they aren't fresh object literals per render.
const FIELD_ROW_STYLE: React.CSSProperties = {
  display: "grid",
  gap: "8px",
  gridTemplateColumns: "1fr auto",
  alignItems: "center",
  width: "100%"
};
// Scoped override (this component only): antd reserves 24px of right padding on a
// multiple + allow-clear selector; drop it so the box content isn't inset on the
// right. `!important` guarantees it regardless of stylesheet injection order.
const SelectCell = styled.div`
  .ant-select-multiple.ant-select-allow-clear .ant-select-selector {
    padding-right: 0 !important;
  }
`;
// Fixed, uniform width across every box; never changes with the selection.
const SELECT_STYLE: React.CSSProperties = {
  width: "200px",
  minWidth: "200px"
};

/**
 * Overflow indicator: after as many tags as fit in the box, show `+N...` for the
 * rest. `maxTagCount="responsive"` re-invokes this with the current omitted set on
 * every layout/selection change, so the count stays accurate.
 */
function renderOverflowCount(omittedValues: readonly unknown[]): JSX.Element {
  return (
    <span aria-label={`${omittedValues.length} more selected`}>
      {`+${omittedValues.length}...`}
    </span>
  );
}

// Keep the tag's mousedown from blurring/closing the select or the tooltip so
// its ✕ stays clickable (data-table pattern).
function preventMouseDown(event: React.MouseEvent<HTMLSpanElement>): void {
  event.preventDefault();
  event.stopPropagation();
}

/**
 * Tooltip body: every selected value for one field, as closable tags (data-table
 * pattern). Closing a tag removes that value from the layer's selection.
 */
function SelectionTags({
  values,
  formatLabel,
  onRemove
}: {
  values: string[];
  formatLabel: (value: string) => string;
  onRemove: (value: string) => void;
}): JSX.Element {
  if (values.length === 0) {
    return (
      <span style={{ fontSize: 13, color: "rgba(0, 0, 0, 0.45)" }}>No selections</span>
    );
  }
  return (
    <div style={{ maxHeight: 320, maxWidth: 300, overflowY: "auto" }}>
      {values.map((v) => (
        <Tag
          key={v}
          closable
          onMouseDown={preventMouseDown}
          onClose={(event) => {
            event.preventDefault();
            onRemove(v);
          }}
          style={{ marginInlineEnd: 4, marginBottom: 4, fontSize: 13 }}>
          {formatLabel(v)}
        </Tag>
      ))}
    </div>
  );
}

/**
 * Attribute multi-selects for the Rig/Frac layer-local filter (EVA-63):
 * operator/basin/subBasin/stateProvince/county/locationType on both layers, plus rigTrajectory
 * on the Rigs layer only (EVA-107 — see FIELDS). Rendered inside the layer's existing
 * settings popover (see MapLayerSettings.tsx) when `layerId` is the Rigs or Frac
 * Crews layer. Selections are layer-scoped — Rigs and Frac Crews never share
 * state (FR-003).
 *
 * Options are CASCADED (EVA-63 revision): each dropdown offers only values still
 * reachable under the other fields' active selections, derived by the
 * useRigFracLayerFilter adapter from the rig/frac features currently on the map
 * and read here from the store — never the whole market domain.
 */
export function RigFracLayerFilter({ layerId }: RigFracLayerFilterProps): JSX.Element {
  const filterOptions = useMapStore(selectRigFracFilterOptions);
  const layerFilterSettings = useMapStore(selectLayerFilterSettings);
  const setLayerFilter = useMapStore((s) => s.setLayerFilter);
  const countyLabel = useRigFracCountyLabel();
  const formatOptionLabel = (key: keyof RigFracLayerFilterSelections, value: string) => {
    if (key === RIG_FRAC_FIELD.County) return countyLabel(value);
    if (key === RIG_FRAC_FIELD.StateProvince) return formatRigFracStateLabel(value);
    return value;
  };

  const options = filterOptions[layerId] ?? {};
  const selections = layerFilterSettings[layerId] ?? {};
  // EVA-107: drop rig-only fields on the Frac Crews layer (see FIELDS.rigOnly).
  const fields = FIELDS.filter(
    ({ rigOnly, awsOnly }) =>
      (!rigOnly || layerId === RIG_LAYER) && (!awsOnly || IS_AWS_ENV)
  );

  return (
    <>
      {fields.map(({ key, label }) => {
        const selected = selections[key] ?? [];
        // Always include the already-selected values, even if the current
        // (viewport-scoped) map facets don't contain them — a prior selection
        // must never silently disappear from the dropdown.
        const optionValues = selected.length
          ? Array.from(new Set([...(options[key] ?? []), ...selected])).sort()
          : options[key] ?? [];
        return (
          <LayerSettingsRowItem key={key}>
            <div style={FIELD_ROW_STYLE}>
              <label>{label}</label>
              <AntdTooltip
                color="rgba(255, 255, 255, 0.98)"
                overlayStyle={{ maxWidth: 320 }}
                // Open to the right of the box so the tooltip never covers the other
                // select rows stacked above/below it.
                placement="right"
                // Hover only. A "focus" trigger stayed open while the Select was
                // focused (clicking an item focuses it), so a map click that closed
                // the popover left the overlay with no mouseleave to fire — it hung in
                // the panel. Hover alone always closes on mouseleave. Click still opens
                // the option dropdown, not the tooltip.
                trigger="hover"
                // Nothing selected => keep it shut so an empty "No selections" tooltip
                // doesn't pop on an incidental pass over the 200px box; otherwise let
                // the trigger drive it.
                open={selected.length ? undefined : false}
                title={
                  <SelectionTags
                    values={selected}
                    formatLabel={(value) => formatOptionLabel(key, value)}
                    onRemove={(value) =>
                      setLayerFilter(
                        layerId,
                        key,
                        selected.filter((v) => v !== value)
                      )
                    }
                  />
                }>
                <SelectCell
                  data-testid={`rigFracFilter-${key}-all`}
                  aria-label={`${label} selected values`}>
                  <Select
                    aria-label={label}
                    data-testid={`rigFracFilter-${key}`}
                    mode="multiple"
                    placeholder={`Filter by ${label}`}
                    value={selected}
                    onChange={(values: string[]) => setLayerFilter(layerId, key, values)}
                    style={SELECT_STYLE}
                    // Fit as many tags as the fixed-width box allows, then show
                    // `+N...` for the rest (recomputed on every selection change).
                    maxTagCount="responsive"
                    maxTagPlaceholder={renderOverflowCount}
                    // The adapter hasn't written this layer's facets yet (tiles still
                    // loading) => "Loading…"; a present-but-empty slice => "No options".
                    notFoundContent={filterOptions[layerId] ? "No options" : "Loading…"}
                    // Render the option dropdown inside this cell so, when the filter
                    // sits in a popover (the layer's Filter button), selecting an option
                    // isn't treated as an outside-click that closes the popover.
                    getPopupContainer={(trigger) =>
                      trigger.parentElement ?? document.body
                    }
                    allowClear>
                    {optionValues.map((value) => (
                      <Select.Option key={value} value={value}>
                        {formatOptionLabel(key, value)}
                      </Select.Option>
                    ))}
                  </Select>
                </SelectCell>
              </AntdTooltip>
            </div>
          </LayerSettingsRowItem>
        );
      })}
    </>
  );
}
